import { useEffect, useRef, useState } from "react";
import { StyledManageActiveResources } from "./ManageActiveResources.styled";
import {
  ACTIVE_RESOURCES_LABEL,
  DELETE_CONFIRM_MESSAGE,
  DELETE_EXERNAL_COLLABORATOR_LABEL,
  EMPTY_COLLABORATORS_MESSAGE,
  ERROR_LABEL,
  ERROR_MESSAGE_LABEL,
  EXTERNAL_COLLABORATOR,
  NAME_SURNAME_EMAIL_LABEL,
} from "../../../../constants/Dictionary";
import {
  AlignmentEnum,
  FilterProps,
  FontWeightEnum,
} from "../../../../types/AppTypes";
import { ReactComponent as PlusIcon } from "../../../../icons/add.svg";
import { BodyTyp } from "../../../../components/Typography/Typography";
import { DoubleStructure } from "../../../../components/core/DoubleStructure/DoubleStructure";
import SearchInput from "../../../../components/core/SearchInput/SearchInput";
import { StyledManagementPage } from "../../Management.styled";
import { FiltersPanel } from "../../../../components/FiltersPanel/FiltersPanel";
import { Link, LinkSizeEnum } from "../../../../components/core/Link/Link";
import {
  AddConfigExternalCollaboratorRequest,
  ConfigCollaborator,
  UpdateConfigCollaboratorRequest,
  UpdateConfigExternalCollaboratorRequest,
} from "../../../../types/api/configurations.type";
import {
  CollaboratorContractTypeEnum,
  CONFIG_COLLABORATORS_PAGE_SIZE,
  StatusCodes,
} from "../../../../constants/AppConstants";
import { useDebounce } from "use-debounce";
import { getConfigCollaboratorsFiltersTitles } from "../../../../constants/FilterTitles";
import { PopupConfirm } from "../../../../components/PopupConfirm/PopupConfirm";
import { ConfigCollaboratorModal } from "./ConfigCollaboratorModal/ConfigCollaboratorModal";
import { ConfigExternalCollaboratorModal } from "./ConfigExternalCollaboratorModal/ConfigExternalCollaboratorModal";
import { useAppSelector } from "../../../../hooks/store.hook";
import { FiltersSelected } from "../../../../components/FiltersSelected/FiltersSelected";
import { AxiosError } from "axios";
import { SingleErrorResponse } from "../../../../types/api/generic.type";
import { useNotification } from "../../../../hooks/notification.hook";
import { Table, TableThemeEnum } from "../../../../components/core/Table/Table";
import { getConfigActiveResourcesColumns } from "../../../../constants/TableColumns";
import {
  setFiltersActiveResources,
  setPageNumberActiveResources,
  setSearchActiveResources,
} from "../../../../stores/actions/management.actions";
import { useDispatch } from "react-redux";
import {
  useAddExternalCollaborator,
  useDeleteExternalCollaborator,
  useManagementActiveResources,
  useManagementActiveResourcesFilters,
  useUpdateActiveResource,
  useUpdateExternalCollaborator,
} from "../../../../hooks/management/ActiveResources/useManagementActiveResources.hooks";

export const ManageActiveResources = () => {
  const [selectedCollaborator, setSelectedCollaborator] =
    useState<ConfigCollaborator | null>(null);
  const [showCollaboratorModal, setShowCollaboratorModal] = useState(false);
  const [showExternalCollaboratorModal, setShowExternalCollaboratorModal] =
    useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  const { notification } = useNotification();
  const { notification: customNotification } = useNotification();
  const dispatch = useDispatch();

  const {
    searchActiveResources,
    pageNumberActiveResources,
    filtersActiveResources,
  } = useAppSelector((state) => state.management);
  const { orgSettings } = useAppSelector((state) => state.config);

  const [debounceSearchActiveResources] = useDebounce(
    searchActiveResources,
    500,
  );

  const onEdit = (configCollaborator: ConfigCollaborator) => {
    setSelectedCollaborator(configCollaborator);
    if (
      configCollaborator.contractType === CollaboratorContractTypeEnum.EXTERNAL
    ) {
      setShowExternalCollaboratorModal(true);
    } else {
      setShowCollaboratorModal(true);
    }
  };

  const onDelete = (configCollaborator: ConfigCollaborator) => {
    setSelectedCollaborator(configCollaborator);
    setShowDeleteModal(true);
  };

  const { updateActiveResource, isUpdatingCollaborator } =
    useUpdateActiveResource();
  const { deleteExternalCollaborator, isDeletingExternalCollaborator } =
    useDeleteExternalCollaborator();
  const { addExternalCollaborator, isAddingExternalCollaborator } =
    useAddExternalCollaborator();
  const { updateExternalCollaborator, isUpdatingExternalCollaborator } =
    useUpdateExternalCollaborator();

  const isSubmitting =
    isAddingExternalCollaborator ||
    isUpdatingExternalCollaborator ||
    isUpdatingCollaborator ||
    isDeletingExternalCollaborator;

  const deleteConfigExternalCollaborator = async () => {
    if (!selectedCollaborator) return;
    await deleteExternalCollaborator(selectedCollaborator.id);

    setShowDeleteModal(false);
    setSelectedCollaborator(null);
  };

  const { data, isFetching } = useManagementActiveResources({
    pageNumberActiveResources,
    debounceSearchActiveResources,
    filtersActiveResources,
  });

  const { filtersData, isFiltersLoading } =
    useManagementActiveResourcesFilters();

  const joinFilters = Object.values(filtersData ?? []).flat();

  const handleFilters = (filters: FilterProps[]) => {
    dispatch(setPageNumberActiveResources(0));
    dispatch(setFiltersActiveResources(filters));
  };

  const addConfigExternalCollaborator = async (
    data: AddConfigExternalCollaboratorRequest,
  ) => {
    try {
      await addExternalCollaborator(data);
      setShowExternalCollaboratorModal(false);
    } catch (error) {
      console.error(`error adding new external collaborator ${error}`);
      const errorObj = error as AxiosError<SingleErrorResponse>;
      let errorMessageObj = {
        message: ERROR_LABEL,
        description: ERROR_MESSAGE_LABEL,
      };
      if (
        errorObj.response &&
        errorObj.response.status === StatusCodes.CUSTOM_ERROR
      ) {
        errorMessageObj = {
          message: ERROR_LABEL,
          description: errorObj.response.data.message,
        };
      }
      customNotification.error(
        errorMessageObj.message,
        errorMessageObj.description,
      );
    }
  };

  const updateConfigExternalCollaborator = async (
    id: number,
    data: UpdateConfigExternalCollaboratorRequest,
  ) => {
    try {
      await updateExternalCollaborator({ id, data });
      setSelectedCollaborator(null);
      setShowExternalCollaboratorModal(false);
    } catch (error) {
      console.error(`error updating external collaborator ${error}`);
      const errorObj = error as AxiosError<SingleErrorResponse>;
      let errorMessageObj = {
        message: ERROR_LABEL,
        description: ERROR_MESSAGE_LABEL,
      };

      if (
        errorObj.response &&
        errorObj.response.status === StatusCodes.CUSTOM_ERROR
      ) {
        errorMessageObj = {
          message: ERROR_LABEL,
          description: errorObj.response.data.message,
        };
      }
      notification.error(errorMessageObj.message, errorMessageObj.description);
    }
  };

  const updateConfigCollaborator = async (
    id: number,
    data: UpdateConfigCollaboratorRequest,
  ) => {
    await updateActiveResource({ id, data });
    setShowCollaboratorModal(false);
    setSelectedCollaborator(null);
  };

  const isFirstRender = useRef(true);

  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    dispatch(setPageNumberActiveResources(0));
  }, [debounceSearchActiveResources]);

  const filtersAreActive = filtersActiveResources.length > 0;

  return (
    <StyledManageActiveResources>
      <StyledManagementPage>
        <div className="top-column">
          <div className="add-ext-collaborator">
            <Link
              onClick={() => setShowExternalCollaboratorModal(true)}
              icon={<PlusIcon />}
              size={LinkSizeEnum.SMALL}
              disabled={isFetching}
            >
              {EXTERNAL_COLLABORATOR}
            </Link>
          </div>
          <div className="filters-row">
            <DoubleStructure verticalAlignment={AlignmentEnum.CENTER}>
              <BodyTyp variant="2" fontWeight={FontWeightEnum.MEDIUM}>
                {data?.pagination.total} {ACTIVE_RESOURCES_LABEL}
              </BodyTyp>
              <div className="search-container">
                <SearchInput
                  value={searchActiveResources}
                  onChange={(value) =>
                    dispatch(setSearchActiveResources(value))
                  }
                  label={NAME_SURNAME_EMAIL_LABEL}
                  name={"search"}
                  disabled={isFetching}
                />
              </div>
            </DoubleStructure>
            <FiltersPanel
              data={filtersData}
              selectedFilters={filtersActiveResources}
              filtersTitles={getConfigCollaboratorsFiltersTitles()}
              setSelectedFilters={handleFilters}
              loading={isFiltersLoading}
            />
          </div>
          <FiltersSelected
            data={joinFilters}
            filters={filtersActiveResources}
            setFilters={handleFilters}
          />
        </div>
        <div className="table-container">
          <Table<ConfigCollaborator>
            theme={TableThemeEnum.DARK}
            loading={isFetching || isSubmitting}
            rowKey="id"
            headers={getConfigActiveResourcesColumns(orgSettings)}
            scrollY={`calc(100dvh - ${filtersAreActive ? "438px" : "386px"})`}
            rowHasDelete={(row) =>
              row.contractType === CollaboratorContractTypeEnum.EXTERNAL
            }
            onEdit={onEdit}
            onDelete={onDelete}
            data={data?.data ?? []}
            pagination={{
              current: pageNumberActiveResources + 1,
              onChange: (pageNumber) =>
                dispatch(setPageNumberActiveResources(pageNumber - 1)),
              total: data?.pagination.total ?? 0,
              pageSize: CONFIG_COLLABORATORS_PAGE_SIZE,
            }}
            emptyText={EMPTY_COLLABORATORS_MESSAGE}
          />
        </div>
      </StyledManagementPage>
      {selectedCollaborator && showCollaboratorModal && (
        <ConfigCollaboratorModal
          isOpen={showCollaboratorModal}
          collaborator={selectedCollaborator}
          closeModal={() => {
            setShowCollaboratorModal(false);
            setSelectedCollaborator(null);
          }}
          loading={isSubmitting}
          onSave={(data) => {
            selectedCollaborator &&
              updateConfigCollaborator(selectedCollaborator.id, data);
          }}
        />
      )}
      {showExternalCollaboratorModal && (
        <ConfigExternalCollaboratorModal
          collaborator={selectedCollaborator!}
          isOpen={showExternalCollaboratorModal}
          closeModal={() => {
            setShowExternalCollaboratorModal(false);
            setSelectedCollaborator(null);
          }}
          loading={isSubmitting}
          onSave={(data) => {
            selectedCollaborator
              ? updateConfigExternalCollaborator(selectedCollaborator.id, data)
              : addConfigExternalCollaborator(
                  data as AddConfigExternalCollaboratorRequest,
                );
          }}
        />
      )}
      <PopupConfirm
        isOpen={showDeleteModal}
        label={DELETE_EXERNAL_COLLABORATOR_LABEL}
        popupText={`${DELETE_CONFIRM_MESSAGE} ${selectedCollaborator?.firstName} ${selectedCollaborator?.lastName}?`}
        onConfirm={deleteConfigExternalCollaborator}
        onCancel={() => {
          setShowDeleteModal(false);
        }}
        isLoading={isDeletingExternalCollaborator}
      />
    </StyledManageActiveResources>
  );
};
